<template>
    <div class="metrics-admin mt-page">
        <div class="metrics-admin__header">
            <h1>{{ $i18n.gt('HEADING_MANAGE_PATIENT_LOGS') }}</h1>
        </div>

        <data-table ref="dataTable"
                    table-id="patient-table"
                    :show-filters="true"
                    :show-pager="true"
                    :show-details="false"
                    :get-items="getMetrics"
                    :filters="filters"
                    :fields="fields">

            <template v-slot:filters>
                <b-row cols="2">
                    <b-col>
                        <b-form-group horizontal
                                      label="Filter">
                            <b-form-select id="filter-select" placeholder="Select one"
                                           v-model="filters.filter.value"
                                           :options="filterOptions">
                            </b-form-select>
                        </b-form-group>
                    </b-col>
                    <b-col>
                        <b-form-group horizontal
                                      label="First/Last Name">
                            <b-form-input v-model="filters.text.value"/>
                        </b-form-group>
                    </b-col>
                </b-row>
            </template>

            <template v-slot:customField="slotProps">
                <div v-if="slotProps.row.field.key == 'participant'">
                    <patient-id :participant="slotProps.row.item.participant"></patient-id>
                    <b-badge variant="warning" class="mt-1"
                             v-if="slotProps.row.item.participant.lacksPassword">
                        {{ $i18n.gt('BADGE_NO_PASSWORD') }}
                    </b-badge>
                    <b-badge variant="danger" class="ml-3"
                             v-if="slotProps.row.item.disabled">
                        {{ $i18n.gt('LABEL_DISABLED') }}
                    </b-badge>
                </div>
                <div v-else-if="slotProps.row.field.key == 'preferredContact'">
                    <patient-contact-methods-display :participant="slotProps.row.item.participant"></patient-contact-methods-display>
                </div>
                <div v-else-if="slotProps.row.field.key == 'metrics'">
                    {{getMetricInfo(slotProps.row.item)}}
                </div>
                <div v-else-if="slotProps.row.field.key == 'updated'">
                    <timestamp :date="slotProps.row.item.participant.last_metric_date" :includeTime="true"></timestamp>
                </div>
                <div v-else-if="slotProps.row.field.key == 'status'">
                    <b-badge
                        v-if="getStatusBadge(slotProps.row.item)['variant'] != 'none'"
                        :variant="getStatusBadge(slotProps.row.item)['variant']">
                        {{ getStatusBadge(slotProps.row.item)['text'] }}
                    </b-badge>
                </div>
                <div v-else-if="slotProps.row.field.key == 'checkoff'">
                    <rpm-check-modal
                        :on-submit="refreshTable"
                        :provider="slotProps.row.item">
                    </rpm-check-modal>
                </div>
                <div v-else-if="slotProps.row.field.key == 'billing'">
                    <rpm-bill-modal
                        :on-submit="refreshTable"
                        :provider="slotProps.row.item">
                    </rpm-bill-modal>
                </div>
                <div v-else-if="slotProps.row.field.key == 'outreach'">
                   <b-btn variant="danger" size="sm"
                          :to="`/rpm/${practiceId}/createNote/participantId/${slotProps.row.item.participant_id}/type/RPM_OUTREACH`">
                       <shs-icon name="bullhorn"></shs-icon>
                   </b-btn>
                </div>
            </template>
        </data-table>

    </div>
</template>

<script>

import MetricUtil from '@/providers/Metrics'
import Metric from "@/models/Metric";

export default {
    name: "MetricsAdmin",
    props: {
        practiceId: Number,
    },
    data() {
        return {
            filterOptions: [
                { value: null, text: 'All Patients' },
                { value: 'needs_check', text: 'Needs Check' },
                { value: 'checked', text: 'Already Checked' },
                { value: 'needs_outreach', text: 'Needs Outreach'},
                { value: 'ready_for_billing', text: 'Ready for Billing'}
            ],
            fields: [
                {key: 'participant', sortable: true, label: 'Patient'},
                {key: 'preferredContact', sortable: false, label: 'Contact'},
                {key: 'status', sortable: false, custom: true},
                {key: 'updated', sortable: false, custom: true},
                {key: 'metrics', sortable: false, custom: true},
                {key: 'checkoff', custom: true, label: 'Check'},
                {key: 'billing', custom: true, label: 'Bill'},
                // {key: 'overview', custom: true, label: 'Overview'},
                {key: 'outreach', custom: true, label: 'Outreach'}
            ],
            filters: {
                filter: { field: 'filter', type: 'string', value: null, label: 'Filter' },
                text: {field: 'text', type: 'string', value: null, label: 'First/Last Name'},
            },
            devices: []
        }
    },
    methods: {
        getMetrics(listParams) {
            return this.$api.getPatients(this, this.filters, listParams, this.practiceId, ['participant.metrics',
                'participant.needs_rpm_check',
                'participant.needs_outreach',
                'participant.surveyNotes',
                'participant.unbilled_checks',
                'participant.last_rpm_check_date',
                'participant.ready_for_rpm_billing',
                'participant.last_metric_date'])
        },
        refreshTable(){
            this.$refs.dataTable.refresh()
        },
        getStatusBadge(item) {
           if(item.participant.needs_outreach) {
               return {
                   text: 'Needs Outreach',
                   variant: 'danger',
               }
           } else if(item.participant.needs_rpm_check) {
               return {
                   text: 'Needs Check',
                   variant: 'warning',
               }
           } else if(item.participant.ready_for_rpm_billing) {
               return {
                   text: 'Ready for Billing',
                   variant: 'success',
               }
           } else {
               return {
                   text: '',
                   variant: 'none'
               }
           }
        },
        getMetricInfo(item) {
            let info = ""
            let metrics = MetricUtil.availableMetrics(item.participant.metrics)
            metrics.forEach((metric, index) => {
               info += MetricUtil.metricLabels()[metric]
                if(index != metrics.length - 1){
                    info += ', '
                }
            })
            return info
        },
        getMetricUpdated(item) {
            const date = Date.parse(item.participant.last_metric_date)
            return date ? date : null
        }
    },


}
</script>

<style lang="scss">

.metrics-admin__header {
    margin-bottom: .5em;
    display: flex;
    justify-content: space-between;
    align-items: center;
    flex-direction: row;
}

</style>
